import classNames from "classnames";
import React, { FC, MouseEvent, useCallback, useEffect, useState, useRef } from "react";
import { isMobile } from "react-device-detect";
import ScrollArea from "react-scrollbar";
import { usePopup } from "./context/PopupProvider";

type IValue = string | number | readonly string[] | undefined;

type IOption = {
	name: string;
	value?: IValue;
};
interface Props {
	options: IOption[];
	value?: IValue;
	className?: string;
	align?: string;
	onChange?: Function;
}

const SelectBox: FC<Props> = ({ options, value, className, align, onChange }: Props) => {
	const id = useCallback(() => Math.random(), []);
	const [, setActive] = useState(false);
	const [selectValue, setSelectValue] = useState(value);
	const { isActivePopup, togglePopup, closePopup } = usePopup();
	const scrollAreaRef = useRef<ScrollArea>(null);

	/**
	 * value 값의 index
	 */
	const getIndex = useCallback(
		(value: IValue): number => {
			return options.findIndex((option) => {
				return option.value === value;
			});
		},
		[options],
	);
	/**
	 * value 로  Option 가져오기
	 * @param {*} value
	 */
	const getOptionByValue = useCallback(
		(value: IValue): IOption => {
			if (!options || options.length === 0 || Array.isArray(options) === false) {
				return { name: "", value: "" };
			}
			const option: IOption =
				options.find((option) => {
					return option.value === value;
				}) || options[0];

			return option;
		},
		[options],
	);

	useEffect(() => {
		setSelectValue(value);
	}, [value]);

	const handleClick = useCallback(
		(e) => {
			//초기 vale값으로 이동
			setTimeout(() => {
				if (scrollAreaRef && scrollAreaRef.current) {
					const index = getIndex(value);
					scrollAreaRef.current.scrollYTo(index * 35);
				}
			}, 10);
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();
			togglePopup();
		},
		[getIndex, togglePopup, value],
	);

	const handleOptionClick = useCallback(
		(value) => (e: MouseEvent<HTMLButtonElement>) => {
			if (value !== selectValue) {
				setSelectValue(value);
			}
			closePopup();
			if (onChange) {
				onChange(value);
			}
		},
		[closePopup, onChange, selectValue],
	);

	const handleChange = useCallback(
		(e) => {
			const value = e.target[e.target.selectedIndex].value;
			if (value !== selectValue) {
				setSelectValue(value);
			}
			setActive(false);
			if (onChange) {
				onChange(value);
			}
		},
		[onChange, selectValue],
	);

	const selected = getOptionByValue(selectValue);

	useEffect(() => {}, []);

	if (isMobile) {
		return (
			<div className={className}>
				<label htmlFor={`select_${id}`}>{selected.name}</label>
				<select id={`select_${id}`} onChange={handleChange}>
					{Array.isArray(options) &&
						options.map((option, index) => (
							<option key={index} value={option.value} selected={option.value === selectValue}>
								{option.name}{" "}
							</option>
						))}
				</select>
			</div>
		);
	} else {
		return (
			<div className={classNames("select-box", { active: isActivePopup })}>
				<button type="button" className="selected" onClick={handleClick}>
					<span>{selected.name}</span>
				</button>
				<ul className="select-layer" data-align={align}>
					<ScrollArea
						ref={scrollAreaRef}
						speed={0.8}
						style={{ maxHeight: 148 }}
						horizontal={false}
						smoothScrolling
						stopScrollPropagation={true}
					>
						{Array.isArray(options) &&
							options.map((option, index) => (
								<li key={index}>
									<button type="button" onClick={handleOptionClick(option.value)}>
										<span>{option.name} </span>
									</button>
								</li>
							))}
					</ScrollArea>
				</ul>
			</div>
		);
	}
};

export const createOptions = (arr: IValue[]) => {
	return arr.map(
		(val: IValue): IOption => {
			return {
				name: String(val),
				value: val,
			};
		},
	);
};

export default SelectBox;
